// @systeme: contrat
// CE QUE LE MODÈLE A LE DROIT DE RENVOYER après un vocal ou un texte.
//
// Règle R2, vérifiée par script : aucune date ici. Le modèle décrit QUAND avec une
// expression relative (« mardi_prochain », « le_12 », « dans_3_semaines », « demain »),
// que le serveur convertit en Europe/Paris. Il peut donner une heure « HH:MM » : une
// heure n'est pas une date, elle ne dépend d'aucun calendrier.
import { z } from 'zod';
import { SchemaCategorieElement, SchemaChampFiche, SchemaRoleFamille, SchemaSensibilite, } from '../../enumerations.js';
/** Une heure « HH:MM » (dupliquée ici pour que ce fichier reste autonome et lisible). */
const SchemaHeureLlm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const SchemaFicheExtraite = z.object({
    /** Court, à l'infinitif ou nominal : « Dentiste pour Léa », « Racheter du produit lave-vaisselle ». */
    titre: z.string().min(1).max(200),
    /** « tache » : à faire ; « evenement » : un rendez-vous ou une date ; « note » : à garder ; « course » : à acheter. */
    type: z.enum(['tache', 'evenement', 'note', 'course']),
    categorie: SchemaCategorieElement.nullable(),
    /**
     * Quand, en expression relative comprise par le serveur, ou null si rien n'est dit.
     * Jamais une date : « mardi », « mardi_prochain », « demain », « le_12 », « le_12_mars »,
     * « dans_3_jours », « dans_2_semaines », « ce_week_end », « la_semaine_prochaine »…
     */
    expression_date: z.string().max(60).nullable(),
    heure: SchemaHeureLlm.nullable(),
    /**
     * Si ça revient : « chaque_jour », « chaque_semaine », « chaque_mardi »,
     * « chaque_lundi_et_jeudi », « une_semaine_sur_deux », « chaque_mois », « chaque_annee ».
     * Null si c'est une fois. Jamais une date : le serveur calcule.
     */
    expression_recurrence: z.string().max(60).nullable(),
    /**
     * Une date LIMITE plutôt qu'une date (« il faut que ce soit fait avant… ») :
     * « avant_les_vacances », « avant_la_rentree », « avant_noel », « avant_la_fin_du_mois »,
     * « avant_le_week_end », « avant_vendredi », « avant_le_15 », « avant_le_12_mars »,
     * « avant_les_vacances_de_noel ». Null sinon. Une tâche « avant X » a expression_date null.
     */
    expression_butoir: z.string().max(60).nullable(),
    /** Le prénom ou le rôle tel qu'il a été dit : « Léa », « ma sœur », « le dentiste ». */
    personne: z.string().max(60).nullable(),
    role_personne: SchemaRoleFamille.nullable(),
    detail: z.string().max(1000).nullable(),
    /** « sante » dès qu'il s'agit de médecin, médicament, symptôme, ordonnance. */
    sensibilite: SchemaSensibilite,
    /** 0 à 1 : à quel point le modèle est sûr de cette fiche. */
    confiance: z.number().min(0).max(1),
    /** Les hypothèses incertaines, en clair, pour les afficher en italique avec un « ? ». */
    doutes: z.array(z.string().max(200)).max(5),
    /** Ce qu'il faudra demander. */
    champs_manquants: z.array(SchemaChampFiche).max(4),
});
export const SchemaSortieAnalyse = z.object({
    /** Une fiche par sujet. Un vocal qui parle de trois choses donne trois fiches. */
    fiches: z.array(SchemaFicheExtraite).max(12),
    /** 0 à 1 : la confiance d'ensemble ; sous le seuil, on demande à un modèle plus fort. */
    confiance: z.number().min(0).max(1),
});
/** Après « non, c'est mardi prochain » : la fiche corrigée, entière. */
export const SchemaSortieCorrection = z.object({
    fiche: SchemaFicheExtraite,
    confiance: z.number().min(0).max(1),
});
/**
 * Après la photo d'un document (mot d'école, facture) : ce que le modèle a lu, en clair,
 * et les fiches qu'il en tire. Un document « santé » n'arrive jamais ici (DEC-019).
 */
export const SchemaSortiePhoto = SchemaSortieAnalyse.extend({
    /** Le contenu du document en quelques phrases, pour que l'utilisatrice vérifie sans rouvrir la photo. */
    lecture: z.string().max(2000),
});
//# sourceMappingURL=capture.js.map